import { authState as authStateAtom } from '@/state/auth.state';
import logger from '@/util/logger';
import { auth } from '@rock/db-admin';
import { GoogleAuthProvider, signInWithPopup, signOut, type User } from 'firebase/auth';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';

const log = logger('useAuth');

type AuthState = {
    user: User | null;
    loading: boolean;
    error: string | null;
    signInWithGoogle: () => void;
    logout: () => void;
};

export const provider = new GoogleAuthProvider();
provider.setCustomParameters({
    hd: 'rockhome.app',
    prompt: 'select_account',
});

export const useAuth = (): AuthState => {
    const [authState, setAuthState] = useAtom(authStateAtom);
    const [error, setError] = useState<string | null>(null);

    // Google Sign-In
    const signInWithGoogle = useCallback(() => {
        setAuthState({ user: null, loading: true });
        signInWithPopup(auth, provider)
            .then((result) => {
                setAuthState({ user: result.user, loading: false });
                setError(null);
            })
            .catch((err) => {
                log.error(`Google Sign-In Error: ${err.message}`, { err });
                setError(err.message);
                setAuthState({ user: null, loading: false });
            });
    }, [setAuthState]);

    // Sign Out
    const logout = useCallback(() => {
        signOut(auth)
            .then(() => setAuthState({ user: null, loading: false }))
            .catch((err) => {
                log.error(`Sign Out Error: ${err.message}`, { err });
                setError(err.message);
            });
    }, [setAuthState]);

    return {
        user: authState.user,
        loading: authState.loading,
        error,
        signInWithGoogle,
        logout,
    };
};
